import { useState } from 'react';
import { api, can, getUser } from '../lib/api';
import { dateTime, number, relative, localZoneLabel } from '../lib/format';
import {
  Card, Empty, ErrorNote, Field, Hash, Loading, Modal, PageHead, Pill, SecretReveal, useResource, useToast,
} from '../components/ui';
import { RowActions } from '../components/RowActions';

/**
 * CM-6 Withdrawal propagation (wireframe W7).
 *
 * S.6(6) makes the fiduciary responsible for its processors stopping too, so
 * this screen is about the loop closing: who acknowledged, who is retrying, who
 * failed. The delivery trail feeds the evidence export.
 *
 * An endpoint that fails ten deliveries in a row is paused by the dispatcher;
 * its events are held until it is resumed. Each delivery opens into its
 * attempt log.
 */

interface Subscription {
  id: string;
  name: string;
  url: string;
  description: string;
  events: string[];
  status: 'active' | 'paused' | 'disabled';
  lastDeliveredAt: string | null;
  lastError: string | null;
  events7d: number;
  delivered: number;
  failed: number;
  pending: number;
  held: number;
  dead: number;
  stale: number;
  consecutiveFailures: number;
  pausedAt: string | null;
  pausedReason: 'failures' | 'manual' | null;
  autoAcknowledge: boolean;
  ackWithinHours: number;
  secretRotatedAt: string | null;
  previousSecretValidUntil: string | null;
}

interface Delivery {
  id: string;
  subscription_name: string;
  event_id: string;
  action: string;
  status: string;
  attempt: number;
  response_code: number | null;
  response_body: string | null;
  next_attempt_at: string | null;
  delivered_at: string | null;
  created_at: string;
}

interface Attempt {
  id: string;
  kind: 'delivery' | 'test';
  attempt: number;
  attemptedAt: string;
  durationMs: number | null;
  responseCode: number | null;
  outcome: 'delivered' | 'failed';
  error: string | null;
  responseExcerpt: string | null;
  signatures: number;
}

/** A processor told by hand, or an endpoint's reply. Same vocabulary as a delivery: confirmed is delivered. */
interface Acknowledgement {
  id: string;
  kind: 'manual' | 'webhook';
  processorId: string | null;
  processorName: string;
  eventId: string;
  action: string;
  purposeName: string | null;
  principalHash: string;
  occurredAt: string;
  toldAt: string;
  toldBy: string;
  channel: string;
  confirmedAt: string | null;
  confirmedBy: string | null;
  evidenceAttachmentId: string | null;
  evidenceFilename: string | null;
  note: string;
  recordedBy: string;
  status: 'delivered' | 'pending';
}

interface ProcessorRow { id: string; name: string; status: string }
interface LedgerEvent { event_id: string; action: string; occurred_at: string; principal_hash: string; purpose_name: string | null }
interface AttachmentRow { id: string; filename: string; uploadedAt: string }

const ACK_CHANNELS: Array<[string, string]> = [
  ['email', 'Email'], ['phone', 'Phone'], ['letter', 'Letter'], ['meeting', 'Meeting'], ['portal', 'Processor portal'], ['other', 'Other'],
];
const CHANNEL_LABEL: Record<string, string> = { ...Object.fromEntries(ACK_CHANNELS), webhook: 'Webhook reply' };

const EVENT_TYPES = ['granted', 'declined', 'withdrawn', 'expired', 'refreshed', 'modified'] as const;

const DELIVERY_FILTERS: Array<[string, string]> = [
  ['', 'All'], ['pending', 'Pending'], ['retrying', 'Retrying'], ['held', 'Held'], ['delivered', 'Delivered'],
  ['dead', 'Dead'], ['stale', 'Stale'], ['skipped', 'Skipped'],
];

const DELIVERY_STATUS: Record<string, { label: string; tone?: 'ok' | 'warn' | 'bad' }> = {
  pending: { label: 'Pending', tone: 'warn' },
  retrying: { label: 'Retrying', tone: 'warn' },
  held: { label: 'Held', tone: 'warn' },
  delivered: { label: 'Delivered', tone: 'ok' },
  failed: { label: 'Dead', tone: 'bad' },
  dead: { label: 'Dead', tone: 'bad' },
  stale: { label: 'Stale' },
  skipped: { label: 'Skipped' },
};

/** Statuses an operator can send again while the endpoint is active. */
const RETRYABLE = new Set(['retrying', 'failed', 'dead', 'stale', 'skipped']);

function subscriptionStatus(s: Subscription): { label: string; tone?: 'ok' | 'warn' | 'bad' } {
  if (s.status === 'paused') return { label: 'Paused', tone: 'warn' };
  if (s.status === 'disabled') return { label: 'Disabled' };
  if (s.failed > 0) return { label: 'Failing', tone: 'bad' };
  if (s.pending > 0) return { label: 'Retrying', tone: 'warn' };
  return { label: 'Active', tone: 'ok' };
}

export default function Propagation() {
  const writable = can(getUser(), 'processors:edit');
  const processorsWritable = writable;
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [recording, setRecording] = useState(false);
  const [confirming, setConfirming] = useState<Acknowledgement | null>(null);
  const [resuming, setResuming] = useState<Subscription | null>(null);
  const [rotating, setRotating] = useState<Subscription | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ name: string; key: string; note: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [nonce, setNonce] = useState(0);

  const subs = useResource<{ subscriptions: Subscription[] }>(() => api.get('/v1/webhooks/subscriptions'), [nonce]);
  const deliveries = useResource<{ deliveries: Delivery[] }>(
    () => api.get(`/v1/webhooks/deliveries?limit=100${statusFilter ? `&status=${statusFilter}` : ''}`),
    [statusFilter, nonce],
  );
  const acks = useResource<{ acknowledgements: Acknowledgement[] }>(
    () => api.get(`/v1/processors/acknowledgements?limit=100${statusFilter === 'delivered' || statusFilter === 'pending' ? `&status=${statusFilter}` : ''}`),
    [statusFilter, nonce],
  );

  async function act(fn: () => Promise<unknown>, message: string) {
    try {
      await fn();
      toast(message, 'ok');
      setNonce((n) => n + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  async function sendTest(s: Subscription) {
    try {
      const r = await api.post<{ ok: boolean; responseCode: number | null; durationMs: number; error: string | null }>(`/v1/webhooks/subscriptions/${s.id}/test`);
      toast(r.ok
        ? `Test delivered to ${s.name}: ${r.responseCode}, ${number(r.durationMs)} ms.`
        : `Test to ${s.name} failed: ${r.responseCode ?? r.error ?? 'no response'}.`, r.ok ? 'ok' : 'bad');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const paused = (subs.data?.subscriptions ?? []).filter((s) => s.status === 'paused');

  return (
    <>
      <PageHead
        eyebrow="Data processors" backTo="/processors" title="Delivery log"
      >
        <button onClick={() => act(() => api.post('/v1/webhooks/dispatch'), 'Dispatch run complete.')}>
          Run dispatch now
        </button>
        {writable && <button className="primary" onClick={() => setAdding(true)}>Add subscriber</button>}
      </PageHead>

      <ErrorNote error={subs.error} />
      {secret && (
        <SecretReveal label={`Signing secret · ${secret.name}`} secret={secret.key} note={secret.note} />
      )}

      {paused.map((s) => (
        <div className="note warn" key={s.id} role="status" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <strong>{s.name} paused</strong>
            <div className="small">
              {s.pausedReason === 'failures' ? `${number(s.consecutiveFailures)} failed deliveries in a row` : 'Paused by an operator'}
              {s.pausedAt ? ` · ${dateTime(s.pausedAt)}` : ''} · {number(s.held)} held
              {s.lastError ? ` · last response ${s.lastError.split(':')[0]}` : ''}
            </div>
          </div>
          {writable && <button className="small" onClick={() => sendTest(s)}>Send test</button>}
          {writable && <button className="primary small" onClick={() => setResuming(s)}>Resume</button>}
        </div>
      ))}

      {subs.loading && !subs.data && <Loading what="Loading subscribers" />}

      {subs.data && (subs.data.subscriptions.length === 0 ? (
        <Empty>No subscribers.</Empty>
      ) : (
        <Card title="Endpoints">
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Subscriber</th>
                  <th>Endpoint</th>
                  <th>Events</th>
                  <th className="num">7d</th>
                  <th className="num">Delivered</th>
                  <th className="num">Held</th>
                  <th className="num">Dead</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {subs.data.subscriptions.map((s) => {
                  const st = subscriptionStatus(s);
                  return (
                    <tr key={s.id}>
                      <td>
                        <strong>{s.name}</strong>
                        {s.lastError && <div className="small" style={{ color: 'var(--bad)' }}>{s.lastError}</div>}
                        {s.lastDeliveredAt && <div className="small muted">last delivery {relative(s.lastDeliveredAt)}</div>}
                        {s.autoAcknowledge && <div className="small muted">Acknowledges by reply · within {s.ackWithinHours} h</div>}
                        {s.previousSecretValidUntil && <div className="small muted">Previous secret valid until {dateTime(s.previousSecretValidUntil)}</div>}
                      </td>
                      <td className="mono small" style={{ maxWidth: 260, wordBreak: 'break-all' }}>{s.url}</td>
                      <td>{s.events.map((e) => <span className="chip" key={e}>{e}</span>)}</td>
                      <td className="num">{number(s.events7d)}</td>
                      <td className="num">{number(s.delivered)}</td>
                      <td className="num">{number(s.held)}</td>
                      <td className="num">{number(s.dead)}</td>
                      <td><Pill tone={st.tone}>{st.label}</Pill></td>
                      <td>
                        {writable && (
                          <RowActions
                            primary={s.status === 'paused'
                              ? <button className="small" onClick={() => setResuming(s)}>Resume</button>
                              : <button className="small" onClick={() => sendTest(s)}>Send test</button>}
                            more={[
                              ...(s.status === 'active' ? [{
                                label: 'Pause', onClick: () => act(() => api.post(`/v1/webhooks/subscriptions/${s.id}/status`, { status: 'paused' }), `${s.name} paused.`),
                              }] : []),
                              ...(s.status !== 'active' ? [{ label: 'Send test', onClick: () => sendTest(s) }] : []),
                              {
                                label: 'Redeliver failed', disabled: s.status !== 'active' || s.dead === 0,
                                onClick: () => act(async () => {
                                  const r = await api.post<{ redelivered: number }>(`/v1/webhooks/subscriptions/${s.id}/redeliver`, {});
                                  await api.post('/v1/webhooks/dispatch');
                                  return r;
                                }, `Failed deliveries to ${s.name} queued again.`),
                              },
                              ...(s.stale > 0 ? [{
                                label: 'Send stale', disabled: s.status !== 'active',
                                onClick: () => act(() => api.post(`/v1/webhooks/subscriptions/${s.id}/redeliver`, { include: ['stale'] }), `Stale deliveries to ${s.name} queued.`),
                              }] : []),
                              { label: 'Rotate secret', onClick: () => setRotating(s) },
                              {
                                label: s.autoAcknowledge ? 'Turn off acknowledgement by reply' : 'Turn on acknowledgement by reply',
                                onClick: () => act(() => api.put(`/v1/webhooks/subscriptions/${s.id}/settings`, { autoAcknowledge: !s.autoAcknowledge }),
                                  s.autoAcknowledge ? 'Acknowledgement by reply off.' : 'Acknowledgement by reply on.'),
                              },
                            ]}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      <Card title="Deliveries" style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {DELIVERY_FILTERS.map(([s, label]) => (
            <button
              key={s || 'all'}
              className={`small ${statusFilter === s ? 'primary' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {label}
            </button>
          ))}
        </div>

        {deliveries.loading && !deliveries.data && <Loading what="Loading deliveries" />}
        {deliveries.data && (deliveries.data.deliveries.length === 0 ? (
          <Empty>No deliveries.</Empty>
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Subscriber</th>
                  <th>Event</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th className="num">Attempt</th>
                  <th>Response</th>
                  <th>Next / delivered</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {deliveries.data.deliveries.map((d) => {
                  const st = DELIVERY_STATUS[d.status] ?? { label: d.status };
                  return (
                    <tr key={d.id}>
                      <td>{d.subscription_name}</td>
                      <td><Hash value={d.event_id} /></td>
                      <td className="small">{d.action}</td>
                      <td><Pill tone={st.tone}>{st.label}</Pill></td>
                      <td className="num">{d.attempt}</td>
                      <td className="small">
                        {d.response_code ?? '—'}
                        {d.response_body && <div className="muted" style={{ maxWidth: 200 }}>{d.response_body.slice(0, 80)}</div>}
                      </td>
                      <td className="small mono">
                        {d.delivered_at ? dateTime(d.delivered_at) : d.next_attempt_at ? `retry ${relative(d.next_attempt_at)}` : '—'}
                      </td>
                      <td>
                        <RowActions
                          primary={<button className="small" onClick={() => setDetail(d.id)}>Attempts</button>}
                          more={writable && RETRYABLE.has(d.status)
                            ? [{ label: 'Retry now', onClick: () => act(() => api.post(`/v1/webhooks/deliveries/${d.id}/retry`), 'Retried.') }]
                            : []}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </Card>

      <Card title="Acknowledgements" style={{ marginTop: 14 }}>
        {processorsWritable && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button onClick={() => setRecording(true)}>Record acknowledgement</button>
          </div>
        )}
        <ErrorNote error={acks.error} />
        {acks.loading && !acks.data && <Loading what="Loading acknowledgements" />}
        {acks.data && (acks.data.acknowledgements.length === 0 ? (
          <Empty>No acknowledgements.</Empty>
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Decision</th>
                  <th>Person</th>
                  <th>Told</th>
                  <th>Status</th>
                  <th>Confirmed</th>
                  <th>Evidence</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {acks.data.acknowledgements.map((a) => (
                  <tr key={a.id}>
                    <td><strong>{a.processorName}</strong></td>
                    <td className="small">
                      {a.action}{a.purposeName ? ` · ${a.purposeName}` : ''}
                      <div className="muted">{dateTime(a.occurredAt)}</div>
                    </td>
                    <td className="small"><span className="mono">{a.principalHash.slice(0, 12)}</span> hashed</td>
                    <td className="small">
                      {dateTime(a.toldAt)}
                      <div className="muted">{CHANNEL_LABEL[a.channel] ?? a.channel}{a.kind === 'manual' ? ` · ${a.toldBy}` : ''}</div>
                    </td>
                    <td><Pill tone={a.status === 'delivered' ? 'ok' : 'warn'}>{a.status === 'delivered' ? 'Confirmed' : 'Told, not confirmed'}</Pill></td>
                    <td className="small">
                      {a.confirmedAt ? <>{dateTime(a.confirmedAt)}<div className="muted">{a.confirmedBy}</div></> : '—'}
                    </td>
                    <td className="small">
                      {a.evidenceAttachmentId
                        ? <button className="small" onClick={() => api.download(`/v1/attachments/${a.evidenceAttachmentId}/download`, a.evidenceFilename ?? 'evidence').catch((e) => toast((e as Error).message, 'bad'))}>{a.evidenceFilename ?? 'Download'}</button>
                        : <span className="muted">None</span>}
                      {a.note && <div className="muted" style={{ maxWidth: 220 }}>{a.note}</div>}
                    </td>
                    <td>
                      {processorsWritable && a.status === 'pending' && a.processorId && (
                        <button className="small" onClick={() => setConfirming(a)}>Record confirmation</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </Card>

      {adding && (
        <AddSubscriber
          onClose={() => setAdding(false)}
          onAdded={(name, key) => {
            setAdding(false);
            setSecret({ name, key, note: 'Signature header: X-Wekalp-Signature: t=<unix>,v1=HMAC-SHA256(secret, "<t>.<raw body>"). Reject timestamps older than 5 minutes.' });
            setNonce((n) => n + 1);
          }}
        />
      )}
      {resuming && (
        <ResumeEndpoint
          sub={resuming}
          onClose={() => setResuming(null)}
          onResumed={(message) => { setResuming(null); toast(message, 'ok'); setNonce((n) => n + 1); }}
        />
      )}
      {rotating && (
        <RotateSecret
          sub={rotating}
          onClose={() => setRotating(null)}
          onRotated={(key, until) => {
            setRotating(null);
            setSecret({
              name: rotating.name, key,
              note: until
                ? `Previous secret valid until ${dateTime(until)}. Until then the signature header carries one v1 per secret; accept a request when any one matches.`
                : 'Previous secret no longer valid.',
            });
            setNonce((n) => n + 1);
          }}
        />
      )}
      {detail && <DeliveryDetail id={detail} onClose={() => setDetail(null)} />}
      {recording && (
        <RecordAcknowledgement
          onClose={() => setRecording(false)}
          onRecorded={() => { setRecording(false); toast('Acknowledgement recorded.', 'ok'); setNonce((n) => n + 1); }}
        />
      )}
      {confirming && (
        <ConfirmAcknowledgement
          ack={confirming}
          onClose={() => setConfirming(null)}
          onConfirmed={() => { setConfirming(null); toast('Confirmation recorded.', 'ok'); setNonce((n) => n + 1); }}
        />
      )}
    </>
  );
}

/** Resume with one of three choices for what was held while paused. */
function ResumeEndpoint({ sub, onClose, onResumed }: { sub: Subscription; onClose: () => void; onResumed: (message: string) => void }) {
  const [mode, setMode] = useState<'queued' | 'failed' | 'none'>('queued');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function resume() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ released: number; resent: number; skipped: number }>(`/v1/webhooks/subscriptions/${sub.id}/resume`, { deliver: mode });
      onResumed(mode === 'none'
        ? `${sub.name} resumed. ${number(r.skipped)} held deliveries skipped.`
        : `${sub.name} resumed. ${number(r.released + r.resent)} deliveries queued.`);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  const options: Array<[typeof mode, string, string]> = [
    ['queued', 'Send held deliveries', `${number(sub.held)} held`],
    ['failed', 'Send held deliveries and resend dead ones', `${number(sub.held)} held, ${number(sub.dead)} dead`],
    ['none', 'Skip held deliveries', `${number(sub.held)} marked skipped; new events are sent`],
  ];

  return (
    <Modal
      title={`Resume ${sub.name}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={resume} disabled={busy}>{busy ? 'Resuming' : 'Resume'}</button>
        </>
      }
    >
      {options.map(([key, label, fact]) => (
        <label key={key} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '6px 0', fontWeight: 400 }}>
          <input type="radio" name="resume-mode" style={{ width: 'auto', marginTop: 3 }} checked={mode === key} onChange={() => setMode(key)} />
          <span>{label}<div className="small muted">{fact}</div></span>
        </label>
      ))}
      <div className="small muted" style={{ marginTop: 6 }}>Held events older than 30 days are marked stale and not sent.</div>
      <ErrorNote error={error} />
    </Modal>
  );
}

function RotateSecret({ sub, onClose, onRotated }: { sub: Subscription; onClose: () => void; onRotated: (secret: string, until: string | null) => void }) {
  const [graceHours, setGraceHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function rotate() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ secret: string; previousValidUntil: string | null }>(`/v1/webhooks/subscriptions/${sub.id}/rotate-secret`, { graceHours });
      onRotated(r.secret, r.previousValidUntil);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Rotate secret · ${sub.name}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={rotate} disabled={busy}>{busy ? 'Rotating' : 'Rotate secret'}</button>
        </>
      }
    >
      <Field label="Previous secret valid for" help="Deliveries are signed with both secrets until then.">
        <select value={graceHours} onChange={(e) => setGraceHours(Number(e.target.value))}>
          <option value={0}>Ends now</option>
          <option value={1}>1 hour</option>
          <option value={24}>24 hours</option>
          <option value={72}>3 days</option>
          <option value={168}>7 days</option>
        </select>
      </Field>
      <ErrorNote error={error} />
    </Modal>
  );
}

const ATTEMPT_ROWS_LIMIT = 50;

function DeliveryDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const loaded = useResource<{ delivery: Delivery & { occurred_at: string }; attempts: Attempt[]; acknowledgement: Acknowledgement | null }>(
    () => api.get(`/v1/webhooks/deliveries/${id}`), [id],
  );
  const d = loaded.data?.delivery;
  const st = d ? DELIVERY_STATUS[d.status] ?? { label: d.status } : null;
  return (
    <Modal title="Delivery" wide onClose={onClose} footer={<button onClick={onClose}>Close</button>}>
      <ErrorNote error={loaded.error} />
      {loaded.loading && !loaded.data && <Loading what="Loading attempts" />}
      {d && st && loaded.data && (
        <>
          <dl className="kv">
            <dt>Subscriber</dt><dd>{d.subscription_name}</dd>
            <dt>Event</dt><dd>{d.action} · {dateTime(d.occurred_at)} · <Hash value={d.event_id} /></dd>
            <dt>Status</dt><dd><Pill tone={st.tone}>{st.label}</Pill></dd>
            <dt>Delivered</dt><dd>{d.delivered_at ? dateTime(d.delivered_at) : '—'}</dd>
            {loaded.data.acknowledgement && (
              <><dt>Acknowledged</dt><dd>{dateTime(loaded.data.acknowledgement.confirmedAt)}{loaded.data.acknowledgement.note ? ` · ${loaded.data.acknowledgement.note}` : ''}</dd></>
            )}
          </dl>
          {loaded.data.attempts.length === 0 ? <Empty>No attempts.</Empty> : (
            <div className="table-scroll" style={{ marginTop: 12 }}>
              <table className="data">
                <thead><tr><th className="num">Attempt</th><th>At ({localZoneLabel()})</th><th>Outcome</th><th>Response</th><th className="num">Duration</th><th className="num">Signatures</th></tr></thead>
                <tbody>
                  {loaded.data.attempts.slice(0, ATTEMPT_ROWS_LIMIT).map((a) => (
                    <tr key={a.id}>
                      <td className="num">{a.attempt}</td>
                      <td className="small mono">{dateTime(a.attemptedAt)}</td>
                      <td><Pill tone={a.outcome === 'delivered' ? 'ok' : 'bad'}>{a.outcome === 'delivered' ? 'Delivered' : 'Failed'}</Pill></td>
                      <td className="small">
                        {a.responseCode ?? 'No response'}
                        {(a.error || a.responseExcerpt) && <div className="muted" style={{ maxWidth: 320, wordBreak: 'break-word' }}>{(a.error ?? a.responseExcerpt ?? '').slice(0, 160)}</div>}
                      </td>
                      <td className="num small">{a.durationMs === null ? '—' : `${number(a.durationMs)} ms`}</td>
                      <td className="num small">{a.signatures}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

const toLocalInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

/** Which processor, which decision, when and how it was told; the confirmation and evidence when already in hand. */
function RecordAcknowledgement({ onClose, onRecorded }: { onClose: () => void; onRecorded: () => void }) {
  const [processorId, setProcessorId] = useState('');
  const [eventId, setEventId] = useState('');
  const [toldAt, setToldAt] = useState(toLocalInput(new Date()));
  const [toldBy, setToldBy] = useState(getUser()?.email ?? '');
  const [channel, setChannel] = useState('email');
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedAt, setConfirmedAt] = useState(toLocalInput(new Date()));
  const [confirmedBy, setConfirmedBy] = useState('');
  const [evidenceId, setEvidenceId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processors = useResource<{ processors: ProcessorRow[] }>(() => api.get('/v1/processors'), []);
  // The decisions a processor is usually told about. Needs ledger:view; without it the event id is typed.
  const events = useResource<LedgerEvent[]>(async () => {
    const lists = await Promise.all((['withdrawn', 'declined', 'expired'] as const).map((action) =>
      api.get<{ events: LedgerEvent[] }>(`/v1/ledger?action=${action}&limit=40`).then((r) => r.events)));
    return lists.flat().sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : -1)).slice(0, 80);
  }, []);
  const files = useResource<{ attachments: AttachmentRow[] }>(
    () => (processorId ? api.get(`/v1/attachments?entityType=processor&entityId=${processorId}`) : Promise.resolve({ attachments: [] })),
    [processorId],
  );

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/v1/processors/${processorId}/acknowledgements`, {
        eventId: eventId.trim(),
        toldAt: new Date(toldAt).toISOString(),
        toldBy,
        channel,
        ...(confirmed ? { confirmedAt: new Date(confirmedAt).toISOString(), confirmedBy } : {}),
        ...(evidenceId ? { evidenceAttachmentId: evidenceId } : {}),
        note,
      });
      onRecorded();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  const ready = processorId && eventId.trim() && toldAt && toldBy.trim() && (!confirmed || (confirmedAt && confirmedBy.trim()));

  return (
    <Modal
      title="Record acknowledgement"
      description="A processor without an endpoint was told of a decision by hand."
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy || !ready}>{busy ? 'Recording' : 'Record'}</button>
        </>
      }
    >
      <Field label="Processor">
        <select value={processorId} onChange={(e) => { setProcessorId(e.target.value); setEvidenceId(''); }}>
          <option value="">Choose a processor</option>
          {(processors.data?.processors ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.status !== 'active' ? ' (disabled)' : ''}</option>)}
        </select>
      </Field>
      <Field label="Decision told" help={events.error ? 'Paste the event reference from the person\'s record.' : 'Recent withdrawals, refusals and expiries.'}>
        {events.error || (events.data && events.data.length === 0)
          ? <input type="text" value={eventId} onChange={(e) => setEventId(e.target.value)} placeholder="Event reference" />
          : (
            <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">Choose a decision</option>
              {(events.data ?? []).map((ev) => (
                <option key={ev.event_id} value={ev.event_id}>
                  {dateTime(ev.occurred_at)} · {ev.action}{ev.purpose_name ? ` · ${ev.purpose_name}` : ''} · record {ev.principal_hash.slice(0, 10)}
                </option>
              ))}
            </select>
          )}
      </Field>
      <div className="grid cols-2">
        <Field label="Told on"><input type="datetime-local" value={toldAt} max={toLocalInput(new Date())} onChange={(e) => setToldAt(e.target.value)} /></Field>
        <Field label="Channel">
          <select value={channel} onChange={(e) => setChannel(e.target.value)}>
            {ACK_CHANNELS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Told by" help="The person at this organisation who told the processor."><input type="text" value={toldBy} onChange={(e) => setToldBy(e.target.value)} /></Field>
      <label style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '4px 0 10px', fontWeight: 400 }}>
        <input type="checkbox" style={{ width: 'auto' }} checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
        The processor has already confirmed
      </label>
      {confirmed && (
        <div className="grid cols-2">
          <Field label="Confirmed on"><input type="datetime-local" value={confirmedAt} max={toLocalInput(new Date())} onChange={(e) => setConfirmedAt(e.target.value)} /></Field>
          <Field label="Confirmed by" help="Name and role at the processor."><input type="text" value={confirmedBy} onChange={(e) => setConfirmedBy(e.target.value)} /></Field>
        </div>
      )}
      <Field label="Evidence file" help={processorId ? 'Files on the processor\'s record. Add one from Data processors first.' : 'Choose a processor to see its files.'}>
        <select value={evidenceId} onChange={(e) => setEvidenceId(e.target.value)} disabled={!processorId}>
          <option value="">None</option>
          {(files.data?.attachments ?? []).map((f) => <option key={f.id} value={f.id}>{f.filename}</option>)}
        </select>
      </Field>
      <Field label="Note"><input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} /></Field>
      <ErrorNote error={error} />
    </Modal>
  );
}

function ConfirmAcknowledgement({ ack, onClose, onConfirmed }: { ack: Acknowledgement; onClose: () => void; onConfirmed: () => void }) {
  const [confirmedAt, setConfirmedAt] = useState(toLocalInput(new Date()));
  const [confirmedBy, setConfirmedBy] = useState('');
  const [evidenceId, setEvidenceId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const files = useResource<{ attachments: AttachmentRow[] }>(() => api.get(`/v1/attachments?entityType=processor&entityId=${ack.processorId}`), [ack.processorId]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/v1/processors/${ack.processorId}/acknowledgements/${ack.id}/confirm`, {
        confirmedAt: new Date(confirmedAt).toISOString(), confirmedBy, ...(evidenceId ? { evidenceAttachmentId: evidenceId } : {}),
      });
      onConfirmed();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Record confirmation · ${ack.processorName}`}
      description={`Told ${dateTime(ack.toldAt)} by ${CHANNEL_LABEL[ack.channel] ?? ack.channel}.`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy || !confirmedBy.trim() || !confirmedAt}>{busy ? 'Recording' : 'Record'}</button>
        </>
      }
    >
      <Field label="Confirmed on"><input type="datetime-local" value={confirmedAt} max={toLocalInput(new Date())} onChange={(e) => setConfirmedAt(e.target.value)} /></Field>
      <Field label="Confirmed by" help="Name and role at the processor."><input type="text" value={confirmedBy} onChange={(e) => setConfirmedBy(e.target.value)} /></Field>
      <Field label="Evidence file" help="Files on the processor's record.">
        <select value={evidenceId} onChange={(e) => setEvidenceId(e.target.value)}>
          <option value="">{ack.evidenceFilename ? `Keep ${ack.evidenceFilename}` : 'None'}</option>
          {(files.data?.attachments ?? []).map((f) => <option key={f.id} value={f.id}>{f.filename}</option>)}
        </select>
      </Field>
      <ErrorNote error={error} />
    </Modal>
  );
}

function AddSubscriber({
  onClose, onAdded,
}: { onClose: () => void; onAdded: (name: string, secret: string) => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('https://');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<string[]>(['withdrawn', 'declined', 'expired']);
  const [autoAcknowledge, setAutoAcknowledge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ secret: string }>('/v1/webhooks/subscriptions', {
        name, url, description, events, autoAcknowledge,
      });
      onAdded(name, res.secret);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add subscriber"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={create} disabled={busy || !name || !url || !events.length}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </>
      }
    >
      <Field label="Name"><input type="text" placeholder="CRM" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="Endpoint URL" help="HTTPS required.">
        <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
      </Field>
      <Field label="Description">
        <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label="Events">
        {EVENT_TYPES.map((t) => (
          <label key={t} style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '4px 0', fontWeight: 400 }}>
            <input
              type="checkbox" style={{ width: 'auto' }}
              checked={events.includes(t)}
              onChange={(e) => setEvents((s) => (e.target.checked ? [...s, t] : s.filter((x) => x !== t)))}
            />
            {t}
          </label>
        ))}
      </Field>
      <label style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '4px 0 10px', fontWeight: 400 }}>
        <input type="checkbox" style={{ width: 'auto' }} checked={autoAcknowledge} onChange={(e) => setAutoAcknowledge(e.target.checked)} />
        Acknowledges by reply
      </label>
      {autoAcknowledge && (
        <div className="small muted" style={{ marginBottom: 10 }}>
          A 2xx reply with {'{"acknowledge": true}'} to a withdrawal, modification or expiry is recorded as the acknowledgement.
        </div>
      )}
      <ErrorNote error={error} />
    </Modal>
  );
}
